import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NotiContent, NotiHeader, NotiBack, NotiTitle, NotiSort, NotiSortDiv, NotiBoard as NotiBoardContainer } from './Notice.styles';
import NotiBoard from 'components/NoticeBoard/NoticeBoard';
import { noticeService, NoticeItem } from 'services/noticeService';

const Notice: React.FC = () => {
  const [sort, setSort] = useState<string>('전체');
  const [notiData, setNotiData] = useState<NoticeItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get universityId from localStorage
        const universityId = localStorage.getItem('universityId');

        if (!universityId) {
          setError('대학 정보를 찾을 수 없습니다.');
          setNotiData([]);
          setLoading(false);
          return;
        }

        // Fetch notices for the university
        const response = await noticeService.getNotices(
          Number(universityId),
          currentPage,
          10,
          'createdAt,desc'
        );

        setNotiData(response.content);
        setTotalPages(response.totalPages);
      } catch (error) {
        console.error('공지사항 데이터를 가져오는 중 오류 발생:', error);
        setError('공지사항을 불러올 수 없습니다.');
        setNotiData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, sort]);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSort(e.target.value);
  };

  const handleBackClick = () => {
    window.history.back();
  };

  const notiClick = (id: number) => {
    console.log(id);
    if (id !== null) {
      navigate(`/notice/${id}`);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <NotiContent>
      <NotiHeader>
        <NotiBack onClick={handleBackClick}>
          <FontAwesomeIcon icon="chevron-left" style={{ fontSize: '1.5em' }} />
        </NotiBack>
        <NotiTitle>공지사항</NotiTitle>
      </NotiHeader>
      <NotiSortDiv>
        <NotiSort value={sort} onChange={handleSortChange}>
          <option value="전체">전체</option>
          <option value="일반">일반</option>
          <option value="취업">취업</option>
          <option value="행사">행사</option>
        </NotiSort>
      </NotiSortDiv>
      <NotiBoardContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            공지사항을 불러오는 중...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            {error}
          </div>
        ) : notiData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            등록된 공지사항이 없습니다.
          </div>
        ) : (
          <>
            {notiData.map((noti) => (
              <NotiBoard key={noti.id} onClick={() => notiClick(noti.id)} id={noti.id} title={noti.title} date={noti.date} />
            ))}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '2rem 0', alignItems: 'center' }}>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    background: currentPage === 0 ? '#f5f5f5' : '#fff',
                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                  }}
                >
                  이전
                </button>
                {(() => {
                  const pageButtons = [];
                  const maxButtons = 5;
                  let startPage = Math.max(0, currentPage - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages - 1, startPage + maxButtons - 1);

                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(0, endPage - maxButtons + 1);
                  }

                  if (startPage > 0) {
                    pageButtons.push(
                      <button
                        key={0}
                        onClick={() => handlePageChange(0)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '1px solid #ddd',
                          background: '#fff',
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                      >
                        1
                      </button>
                    );
                    if (startPage > 1) {
                      pageButtons.push(<span key="ellipsis-start" style={{ padding: '0 0.5rem' }}>...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '1px solid #ddd',
                          background: currentPage === i ? '#007bff' : '#fff',
                          color: currentPage === i ? '#fff' : '#000',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          fontWeight: currentPage === i ? 'bold' : 'normal',
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                  }

                  if (endPage < totalPages - 1) {
                    if (endPage < totalPages - 2) {
                      pageButtons.push(<span key="ellipsis-end" style={{ padding: '0 0.5rem' }}>...</span>);
                    }
                    pageButtons.push(
                      <button
                        key={totalPages - 1}
                        onClick={() => handlePageChange(totalPages - 1)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '1px solid #ddd',
                          background: '#fff',
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pageButtons;
                })()}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages - 1}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    background: currentPage === totalPages - 1 ? '#f5f5f5' : '#fff',
                    cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                  }}
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </NotiBoardContainer>
    </NotiContent>
  );
};

export default Notice;
